import { useEffect, useMemo, useState } from "react";
import { copyText } from "@/lib/clipboard";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, Eyebrow, PageHead, Tile, Pill, Empty, fmtMins, ago } from "@/components/admin/ui";
import { Search, Users, Download, MessageCircle } from "lucide-react";

const SEGMENTS = [
  { id: "all", label: "Everyone" },
  { id: "regulars", label: "Regulars (3+)" },
  { id: "atrisk", label: "At risk" },
  { id: "new", label: "New" },
  { id: "nowa", label: "No number" },
];

export default function Students() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("all");
  const [sort, setSort] = useState("classes");

  useEffect(() => {
    document.title = "Students · Udyog Classes";
    api
      .get("/students")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
  }, []);

  const list = useMemo(() => {
    const all = data?.students || [];
    const filtered = all.filter((s) => {
      if (q.trim() && !`${s.name} ${s.whatsapp}`.toLowerCase().includes(q.trim().toLowerCase())) return false;
      if (seg === "regulars") return s.classes_attended >= 3;
      if (seg === "atrisk") return s.attendance_pct < 50;
      if (seg === "new") return s.classes_attended === 1;
      if (seg === "nowa") return !s.whatsapp;
      return true;
    });
    const key = { classes: "classes_attended", minutes: "total_minutes", attendance: "attendance_pct" }[sort];
    return [...filtered].sort((a, b) =>
      sort === "name" ? String(a.name).localeCompare(String(b.name)) : (b[key] || 0) - (a[key] || 0)
    );
  }, [data, q, seg, sort]);

  const totals = useMemo(() => {
    const all = data?.students || [];
    return {
      count: all.length,
      regulars: all.filter((s) => s.classes_attended >= 3).length,
      withWa: all.filter((s) => s.whatsapp).length,
      minutes: all.reduce((n, s) => n + s.total_minutes, 0),
    };
  }, [data]);

  const copySegment = () => {
    const nums = list.map((s) => s.whatsapp).filter(Boolean);
    if (!nums.length) return toast.error("No numbers in this segment");
    copyText(nums.join(", "));
    toast.success(`${nums.length} numbers copied`);
  };

  const downloadCsv = () => {
    const rows = [
      "Name,WhatsApp,Classes attended,Attendance %,Total minutes,Avg minutes,Messages,Last seen",
      ...list.map((s) =>
        [
          `"${s.name.replace(/"/g, '""')}"`,
          s.whatsapp,
          s.classes_attended,
          s.attendance_pct,
          s.total_minutes,
          s.avg_minutes,
          s.messages,
          s.last_seen || "",
        ].join(",")
      ),
    ].join("\n");
    const url = URL.createObjectURL(new Blob([rows], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "udyog-students.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="admin-students">
      <PageHead
        eyebrow="Students"
        title="Everyone you teach"
        sub={`${totals.count} students across ${data?.total_classes ?? 0} classes`}
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex items-center gap-2 px-4 py-3 w-full sm:w-52 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09]">
              <Search className="w-3.5 h-3.5 text-white/35 shrink-0" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Name or number…"
                className="bg-transparent outline-none text-[12px] text-white placeholder-white/30 w-full"
                data-testid="students-search"
              />
            </div>
            <button
              onClick={copySegment}
              className="px-3.5 py-2.5 rounded-full text-[11px] text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] inline-flex items-center gap-1.5"
              data-testid="students-copy-numbers"
            >
              <MessageCircle className="w-3.5 h-3.5" /> Copy numbers
            </button>
            <button
              onClick={downloadCsv}
              className="px-3.5 py-2.5 rounded-full text-[11px] text-white/70 ring-1 ring-white/12 hover:bg-white/[0.06] inline-flex items-center gap-1.5"
              data-testid="students-export"
            >
              <Download className="w-3.5 h-3.5" /> CSV
            </button>
          </div>
        }
      />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile testid="st-total" label="Students" value={totals.count} />
        <Tile testid="st-regulars" label="Regulars (3+ classes)" value={totals.regulars} tone="good" />
        <Tile
          testid="st-wa"
          label="Reachable on WhatsApp"
          value={totals.withWa}
          hint={totals.count ? `${Math.round((totals.withWa / totals.count) * 100)}% of students` : ""}
        />
        <Tile testid="st-minutes" label="Total teaching watched" value={fmtMins(totals.minutes)} />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap" data-testid="student-segments">
          {SEGMENTS.map((sg) => (
            <button
              key={sg.id}
              onClick={() => setSeg(sg.id)}
              data-testid={`segment-${sg.id}`}
              className={`h-9 px-3.5 rounded-full text-[11.5px] transition-colors ${
                seg === sg.id
                  ? "bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/30"
                  : "text-white/40 ring-1 ring-white/[0.08] hover:text-white"
              }`}
            >
              {sg.label}
            </button>
          ))}
        </div>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          className="h-9 px-3 rounded-full bg-white/[0.04] ring-1 ring-white/[0.09] outline-none text-[11.5px] text-white/70"
          data-testid="students-sort"
        >
          <option value="classes">Most classes</option>
          <option value="minutes">Most minutes</option>
          <option value="attendance">Attendance %</option>
          <option value="name">Name</option>
        </select>
      </div>

      {!data ? (
        <div className="py-16 text-center text-white/35">Loading students…</div>
      ) : list.length === 0 ? (
        <Empty icon={Users} text="No students in this segment yet." testid="students-empty" />
      ) : (
        <Card className="p-5" testid="students-table">
          <div className="overflow-x-auto no-scrollbar">
            <table className="w-full text-[12.5px]">
              <thead>
                <tr className="text-left text-[9.5px] uppercase tracking-[0.18em] text-white/30">
                  <th className="pb-3 pr-3 font-normal">Student</th>
                  <th className="pb-3 pr-3 font-normal">Classes</th>
                  <th className="pb-3 pr-3 font-normal">Attendance</th>
                  <th className="pb-3 pr-3 font-normal">Watched</th>
                  <th className="pb-3 pr-3 font-normal">Chats</th>
                  <th className="pb-3 pr-3 font-normal">Last seen</th>
                  <th className="pb-3 font-normal"> </th>
                </tr>
              </thead>
              <tbody>
                {list.map((s, i) => (
                  <tr
                    key={s.key}
                    className="border-t border-white/[0.05] hover:bg-white/[0.02]"
                    data-testid={`student-row-${i}`}
                  >
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="text-white/85">{s.name}</span>
                        {s.live && <Pill tone="live">in class</Pill>}
                      </div>
                      <div className="text-[11px] text-white/35 truncate max-w-[240px]">
                        {s.class_titles.join(" · ")}
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-white/70">{s.classes_attended}</td>
                    <td className="py-3 pr-3">
                      <div className="flex items-center gap-2">
                        <span className="w-14 h-1.5 rounded-full bg-white/[0.08] overflow-hidden">
                          <span
                            className={`block h-full ${s.attendance_pct >= 50 ? "bg-emerald-400" : "bg-amber-400"}`}
                            style={{ width: `${Math.min(100, s.attendance_pct)}%` }}
                          />
                        </span>
                        <span className="text-white/55">{s.attendance_pct}%</span>
                      </div>
                    </td>
                    <td className="py-3 pr-3 text-white/60 whitespace-nowrap">{fmtMins(s.total_minutes)}</td>
                    <td className="py-3 pr-3 text-white/50">{s.messages || "—"}</td>
                    <td className="py-3 pr-3 text-white/45 whitespace-nowrap">{ago(s.last_seen)}</td>
                    <td className="py-3">
                      {s.whatsapp ? (
                        <a
                          href={`https://wa.me/91${s.whatsapp}`}
                          target="_blank"
                          rel="noreferrer"
                          className="px-3 py-1.5 rounded-full text-[10.5px] text-emerald-300 ring-1 ring-emerald-400/30 hover:bg-emerald-500/10 whitespace-nowrap"
                          data-testid={`student-wa-${i}`}
                        >
                          WhatsApp
                        </a>
                      ) : (
                        <span className="text-[10.5px] text-white/25">no number</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
